import { css, html, nothing } from 'lit';
import 'src/library/elements/d-smooth-resize';
import 'src/library/lists/d-occurrences-list';
import 'src/library/components/d-calendar-year';
import 'src/library/elements/d-section';
import 'src/library/elements/d-wrap';
import 'src/library/elements/d-action';
import 'src/content/event-occurrences/d-create-occurrences';
import 'src/library/fields/d-view-info';
import 'src/library/editors/elements/d-checkbox';
import { customElement, property, query } from 'lit/decorators.js';
import { BaseDialog } from 'src/library/components/BaseDialog';
import { LocalDate } from 'src/utilities/local-date';
import { endYear } from 'src/store';
import { classMap } from 'lit/directives/class-map.js';
import { joinWithAnd } from 'src/utilities/text';
import { t } from 'src/i18n';
import type { Occurrence } from 'src/library/lists/occurrence';
import type { OccurrencesDialogResult } from './occurrences-dialog-result';
import type { OccurrencesDialogInput } from './occurrences-dialog-input';
import type { DCalendarYear } from 'src/library/components/d-calendar-year';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';

@customElement('d-edit-occurrences-dialog')
export class DEditOccurrencesDialog extends BaseDialog<OccurrencesDialogInput, OccurrencesDialogResult> {
  static readonly styles = [
    BaseDialog.styles[0],
    css`
      dialog[open] {
        max-height: calc((var(--vh, 1vh) * 100) - 40px);
      }
      .header-wrapper {
        display: block;
        background: white;
        padding: 0 20px;
        line-height: var(--lineHeightDefault);
        z-index: 2;
      }
      .header {
        align-items: flex-start;
      }
      .sub-title {
        margin-bottom: 6px;
      }
      @container dialog (width > 700px) {
        .header-wrapper {
          padding: 0 30px;
        }
      }
      #settings {
        border-bottom: 1px solid var(--borderColor);
      }
      .actions {
        margin: -6px -8px;
      }
      d-action.active {
        color: black;
      }
      .todo.bordered {
        border-bottom: 1px solid var(--borderColor);
      }
      #occurrences {
        line-height: 100%;
      }
      d-calendar-year {
        margin: 0;
      }
      .body {
        scroll-behavior: smooth;
      }
    `,
  ];
  @property({ type: String })
  eventType = 'standard';
  @property({ type: String })
  eventName = '';
  @property({ type: String })
  parentUuid = '';
  @property({ type: Object })
  modelOccurrence!: Occurrence;
  @property({ type: Array })
  occurrences: Occurrence[] = [];
  @property({ type: Boolean })
  createOccurrencesOpened = false;
  @property({ type: Boolean })
  createRegularOccurrences = false;
  @property({ type: String })
  today = LocalDate.now().toString();
  @property({ type: Boolean })
  showAsList = false;
  @property({ type: Array })
  collectionNames: string[] = [];
  @property({ type: Boolean })
  addToCollections = true;
  @property({ type: Number })
  width = 782;
  @query('.body')
  dialogBodyElm!: HTMLElement;

  protected get calculatedSubtitle(): string {
    if (this.eventType === 'meeting') {
      if (this.occurrences.length === 1) {
        return t('layout.editOccurrences.subtitleRepeatMeeting');
      }
      return t('layout.editOccurrences.subtitleEditMeetingSeries');
    }
    if (this.occurrences.length === 1) {
      return t('layout.editOccurrences.subtitleRepeatTask');
    }
    return t('layout.editOccurrences.subtitleTaskRecurrences');
  }

  protected get calculatedHeaderActions(): DialogActionInput[] {
    return [
      {
        name: this.showAsList ? t('layout.editOccurrences.showAsCalendar') : t('layout.editOccurrences.showAsList'),
        action: 'toggleView',
      },
    ];
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('common:cancel'), action: 'cancel' },
      { name: t('layout.done'), action: 'done', main: true, disabled: this.createOccurrencesOpened },
    ];
  }

  protected async onDispatchAction(disabled: boolean, action: string) {
    if (action === 'toggleView') {
      this.showAsList = !this.showAsList;
    } else {
      return super.onDispatchAction(disabled, action);
    }
  }

  private get eventTerm() {
    if (this.eventType === 'meeting') {
      return t('meetings.eventTerm');
    }
    return t('layout.editOccurrences.eventTermTask');
  }

  private get recurrenceTime() {
    if (this.modelOccurrence.dateTime.split(' ').length === 2) {
      return this.modelOccurrence.dateTime.split(' ')[1];
    }
    return 'NONE';
  }

  private get recurrenceDuration() {
    if (this.modelOccurrence.durationMinutes) {
      return this.modelOccurrence.durationMinutes;
    }
    return 0;
  }

  private get firstEditableDate() {
    if (this.modelOccurrence.dateTime) {
      const firstDateAfterModel = LocalDate.fromString(this.modelOccurrence.dateTime.split(' ')[0]).plusDays(1);
      if (firstDateAfterModel.isSameOrBefore(LocalDate.fromString(this.today))) {
        return this.today;
      }
      return firstDateAfterModel.toString();
    }
    return LocalDate.fromString(this.today).plusDays(1).toString();
  }

  private get recurrenceStartDate() {
    if (this.modelOccurrence.dateTime) {
      const date = LocalDate.fromString(this.modelOccurrence.dateTime.split(' ')[0]);
      return date.plusMonths(1).toString();
    }
    return this.firstEditableDate;
  }

  private get todoOccurrences(): Occurrence[] {
    return this.occurrences
      .filter((o) => {
        return o.dateTime === '';
      })
      .map((o) => {
        return { ...o, disabled: true };
      });
  }

  private get datedOccurrences(): Occurrence[] {
    const modelOccurrenceDate = LocalDate.fromString(this.modelOccurrence.dateTime.split(' ')[0]);
    return this.occurrences
      .filter((o) => {
        return o.dateTime !== '';
      })
      .map((o) => {
        if (
          LocalDate.fromString(o.dateTime.split(' ')[0]).isSameOrBefore(modelOccurrenceDate) ||
          this.isDone(o) ||
          o.restricted
        ) {
          o.disabled = true;
        }
        return o;
      });
  }

  private get hasFutureDeletableOccurrence() {
    return (
      this.datedOccurrences.filter((o) => {
        return (
          LocalDate.fromString(o.dateTime.split(' ')[0]).isSameOrAfter(LocalDate.fromString(this.firstEditableDate)) &&
          !o.disabled
        );
      }).length > 0
    );
  }

  private get collectionsText(): string {
    if (this.collectionNames.length) {
      const eventTerm =
        this.eventType === 'meeting' ? t('layout.editOccurrences.thisMeeting') : t('layout.editOccurrences.thisTask');
      const collectionTerm =
        this.collectionNames.length === 1
          ? t('layout.editOccurrences.collectionSingular')
          : t('layout.editOccurrences.collectionPlural');
      return t('layout.editOccurrences.collectionsText', {
        eventTerm,
        collectionTerm,
        collections: joinWithAnd(this.collectionNames),
      });
    }
    return '';
  }

  scrollToCurrent(e) {
    e.stopPropagation();
    const dialogBodyTop = this.dialogBodyElm.getBoundingClientRect().top;
    this.dialogBodyElm.scrollBy(0, e.detail.value - dialogBodyTop + 1);
  }

  openCreateOccurrences(regular: boolean = false) {
    this.createRegularOccurrences = regular;
    this.createOccurrencesOpened = true;
  }

  closeCreateOccurrences() {
    this.createOccurrencesOpened = false;
  }

  removeFutureOccurrences() {
    const firstEditableDate = LocalDate.fromString(this.firstEditableDate);
    this.occurrences = this.occurrences.filter((o) => {
      if (o.dateTime) {
        return LocalDate.fromString(o.dateTime.split(' ')[0]).isBefore(firstEditableDate) || !this.isDeletable(o);
      }
      return true;
    });
  }

  onOccurrencesCreated(e) {
    e.stopPropagation();
    const occurrences = e.detail.value;
    const newOccurrences = [...this.occurrences];
    occurrences.forEach((o) => {
      const existingOccurrence = this.occurrences.find((e) => {
        return e.dateTime.split(' ')[0] === o.split(' ')[0];
      });
      if (existingOccurrence === undefined) {
        const s = o.split(' ');
        const dateTime = s.length === 1 ? s[0] : s[0] + ' ' + s[1];
        newOccurrences.push({
          occurrenceUuid: '',
          dateTime: dateTime,
          durationMinutes: s.length === 3 ? parseInt(s[2]) : 0,
          classified: this.modelOccurrence.classified,
          restricted: this.modelOccurrence.restricted,
          assignees: this.modelOccurrence.assignees,
        });
      }
    });
    this.occurrences = newOccurrences;
    this.createOccurrencesOpened = false;
  }

  newOccurrence(date: string) {
    let dateTime = date;
    if (this.modelOccurrence.dateTime.split(' ').length === 2) {
      dateTime = date + ' ' + this.modelOccurrence.dateTime.split(' ')[1];
    }
    return {
      occurrenceUuid: '',
      dateTime,
      durationMinutes: this.modelOccurrence.durationMinutes,
      classified: this.modelOccurrence.classified,
      restricted: this.modelOccurrence.restricted,
      assignees: this.modelOccurrence.assignees,
    };
  }

  onDeleteFromList(e) {
    this.toggleOccurrence(e.detail.value);
  }

  toggleOccurrence(dateTime) {
    const existing = this.occurrences.find((o) => {
      return o.dateTime === dateTime;
    });
    if (existing) {
      this.occurrences = this.occurrences.filter((o) => {
        return o.dateTime !== dateTime;
      });
    } else {
      const occurrences = [...this.occurrences];
      occurrences.push(this.newOccurrence(dateTime.split(' ')[0]));
      this.occurrences = occurrences;
    }
  }

  onDayClicked(e) {
    e.preventDefault();
    e.stopPropagation();
    const dateTime = e.detail.value;
    const dayOccurrences = this.occurrences.filter((o) => {
      if (o.dateTime) {
        return o.dateTime.split(' ')[0] === dateTime.split(' ')[0];
      }
      return false;
    });
    if (dayOccurrences.length === 0) {
      this.toggleOccurrence(dateTime);
    } else if (dayOccurrences.length === 1) {
      this.toggleOccurrence(dayOccurrences[0].dateTime);
    }
  }

  onOccurrenceClicked(e) {
    e.preventDefault();
    e.stopPropagation();
    this.toggleOccurrence(e.detail.value);
  }

  yearOccurrences(year) {
    return this.datedOccurrences.filter((e) => {
      return e.dateTime.split('-')[0] === year + '';
    });
  }

  protected renderHeader() {
    return html`
      <div class="header-actions">
        ${this.calculatedHeaderActions.map((a) => {
          return html` <d-action
            @click=${async () => this.onDispatchAction(a.disabled ?? false, a.action)}
            ?disabled=${a.disabled}
            ?delete=${a.action === 'delete'}
            >${a.name}
          </d-action>`;
        })}
      </div>
      <div class="title">
        <div class="sub-title">${this.calculatedSubtitle}</div>
        <div class="main-title">${this.eventName}</div>
      </div>
    `;
  }
  renderBody() {
    const startYear = this.calculateStartYear();
    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }
    return html`
      <d-smooth-resize>
        ${this.showAsList
          ? html`
              <d-occurrences-list
                edit
                .occurrences=${this.datedOccurrences}
                .currentUuid=${this.modelOccurrence.occurrenceUuid}
                @current-occurrence-position=${(e) => this.scrollToCurrent(e)}
                @delete=${(e) => this.onDeleteFromList(e)}
              ></d-occurrences-list>
            `
          : html`
              <div id="calendar">
                ${years.map((year) => {
                  return html`
                    <d-calendar-year
                      edit
                      id="${'year-' + year}"
                      .year=${year}
                      .occurrences=${this.yearOccurrences(year)}
                      .eventTerm=${this.eventTerm}
                      .disableBefore=${this.firstEditableDate}
                      @day-clicked=${(e) => this.onDayClicked(e)}
                      @occurrence-clicked=${(e) => this.onOccurrenceClicked(e)}
                    ></d-calendar-year>
                  `;
                })}
              </div>
            `}
      </d-smooth-resize>
    `;
  }

  firstUpdated() {
    // Determine the current year (or use a property)
    let startDate = LocalDate.fromString(this.firstEditableDate);
    if (startDate.isBefore(LocalDate.fromString(this.today))) {
      startDate = LocalDate.fromString(this.today);
    }
    const startYear = startDate.year();
    const startMonth = startDate.month();

    console.log(startDate.toString(), startYear, startMonth);

    // Query for the element that represents the current year
    const currentEl = this.shadowRoot?.querySelector<DCalendarYear>(`#year-${startYear}`);
    if (currentEl) {
      setTimeout(() => {
        const dialogBodyTop = this.dialogBodyElm.getBoundingClientRect().top;
        const monthTop = currentEl.monthTop(startMonth);
        this.bodyElm.scrollTo({ top: monthTop - dialogBodyTop, behavior: 'instant' });
      }, 10);
    }
  }

  private openCreateActionClass(regular: boolean = false) {
    return {
      active:
        this.createOccurrencesOpened &&
        ((this.createRegularOccurrences && regular) || (!this.createRegularOccurrences && !regular)),
    };
  }

  protected renderPostHeader() {
    let addToCollectionsLabel = t('layout.editOccurrences.addRecurrencesToCollectionSingular');
    if (this.collectionNames.length > 1) {
      addToCollectionsLabel = t('layout.editOccurrences.addRecurrencesToCollectionPlural');
    }
    return html`
      <div class="header-wrapper">
        ${this.collectionNames.length
          ? html` <d-section borderless>
              <d-view-info style="margin-top: 6px;" .content=${this.collectionsText}></d-view-info>
              <d-checkbox
                style="margin-top: -6px;"
                class="fullWidth"
                ?checked="${this.addToCollections}"
                .optionLabel=${addToCollectionsLabel}
                @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => {
                  e.stopPropagation();
                  this.addToCollections = e.detail.checked;
                }}
              ></d-checkbox>
            </d-section>`
          : nothing}
        <div id="settings">
          <d-section>
            <d-wrap tight baseline class="actions fullWidth">
              ${!this.showAsList
                ? html` <div style="margin-left: 8px">${t('layout.editOccurrences.tapInCalendarOr')}</div> `
                : nothing}
              <d-action
                plain
                class=${classMap(this.openCreateActionClass())}
                @click=${() => this.openCreateOccurrences()}
                >${t('layout.editOccurrences.repeatOnce')}</d-action
              >
              <d-action
                plain
                class=${classMap(this.openCreateActionClass(true))}
                @click=${() => this.openCreateOccurrences(true)}
                >${t('layout.editOccurrences.repeatRegularly')}</d-action
              >
              <d-action
                plain
                ?disabled="${!this.hasFutureDeletableOccurrence}"
                @click=${() => this.removeFutureOccurrences()}
                >${t('layout.editOccurrences.deleteFuture')}</d-action
              >
            </d-wrap>
          </d-section>
          <d-smooth-resize>
            ${this.createOccurrencesOpened
              ? html`
                  <d-section>
                    <d-create-occurrences
                      .regular=${this.createRegularOccurrences}
                      .modelDateTime=${this.modelOccurrence.dateTime}
                      .today=${this.today}
                      .startDate=${this.recurrenceStartDate}
                      .time=${this.recurrenceTime}
                      .duration=${this.recurrenceDuration}
                      @done=${(e: CustomEvent<{ value: string[] }>) => this.onOccurrencesCreated(e)}
                      @cancel=${() => this.closeCreateOccurrences()}
                    ></d-create-occurrences>
                  </d-section>
                `
              : nothing}
          </d-smooth-resize>
        </div>
        <div
          class="todo ${classMap({
            bordered: this.todoOccurrences.length > 0,
          })}"
        >
          <d-smooth-resize>
            <d-occurrences-list
              edit
              .occurrences=${this.todoOccurrences}
              .currentUuid=${this.modelOccurrence.occurrenceUuid}
              @delete=${(e) => this.onDeleteFromList(e)}
            ></d-occurrences-list>
          </d-smooth-resize>
        </div>
      </div>
    `;
  }

  protected initializeDialog(input: OccurrencesDialogInput) {
    this.modelOccurrence = input.modelOccurrence;
    this.parentUuid = input.parentUuid;
    this.occurrences = input.occurrences;
    this.eventType = input.eventType;
    this.eventName = input.eventName;
    this.collectionNames = input.collectionNames;
  }

  protected fetchResult(detail: string | undefined): OccurrencesDialogResult {
    return {
      occurrences: detail === 'done' ? this.occurrences : [],
      action: detail === 'done' ? 'done' : 'cancel',
      addToCollections: this.addToCollections,
    };
  }

  private calculateStartYear() {
    let startYear = Number(this.today.split('-')[0]);
    if (this.datedOccurrences.length && this.datedOccurrences[0].dateTime) {
      const firstEventYear = LocalDate.fromString(this.datedOccurrences[0].dateTime.split(' ')[0]).year();
      if (firstEventYear < startYear) {
        startYear = firstEventYear;
      }
    }
    return startYear;
  }

  private isDone(o) {
    return o.doneDate !== undefined && o.doneDate !== null && o.doneDate !== '';
  }

  private isDeletable(occurrence: Occurrence) {
    return (
      !occurrence.restricted &&
      !occurrence.disabled &&
      occurrence.meetingStatus !== 'NOTICE_SENT' &&
      occurrence.meetingStatus !== 'REPORT_WRITTEN'
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-occurrences-dialog': DEditOccurrencesDialog;
  }
}
